import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import mdxComponents from '@/utils/mdxComponents';

type RegistrationFormProps = {
  groups: {
    id: string;
    name: string;
    registrationPageTextSource: MDXRemoteSerializeResult;
  }[];
  initialGroup?: string;
};

const RegistrationForm = ({ groups, initialGroup }: RegistrationFormProps) => {
  const schema = yup
    .object({
      participantFirstName: yup
        .string()
        .trim()
        .required('Feld ist erforderlich'),
      participantLastName: yup
        .string()
        .trim()
        .required('Feld ist erforderlich'),
      participantGroup: yup
        .string()
        .trim()
        .oneOf(groups.map((g) => g.id))
        .required('Feld ist erforderlich'),
      sponsorFirstName: yup.string().required('Feld ist erforderlich'),
      sponsorLastName: yup.string().required('Feld ist erforderlich'),
      sponsorContactMethod: yup
        .string()
        .trim()
        .oneOf(['email', 'post'])
        .required('Feld ist erforderlich'),
      sponsorEmail: yup
        .string()
        .trim()
        .email('Feld muss gültige Email Adresse enthalten')
        .when('sponsorContactMethod', {
          is: 'email',
          then: (s) => s.required('Feld ist erforderlich'),
        }),
      sponsorAddress: yup
        .string()
        .trim()
        .when('sponsorContactMethod', {
          is: 'post',
          then: (s) => s.required('Feld ist erforderlich'),
        }),
      amount: yup
        .number()
        .transform((_value, originalValue) =>
          Number(originalValue.replace(/,/, '.')),
        )
        .typeError('Feld ist erforderlich und muss eine Zahl sein')
        .min(0.5, 'Mindestbetrag beträgt 0.50€')
        .required('Feld ist erforderlich'),
      note: yup.string(),
    })
    .required();

  const {
    handleSubmit,
    register,
    formState: { dirtyFields, errors, isSubmitting },
    watch,
    reset,
  } = useForm({ resolver: yupResolver(schema) });
  const [contactMethod, setContactMethod] = useState<'email' | 'post'>('email');

  const onSubmit = async (values: Record<string, any>) => {
    const response = await fetch('/api/submitRegistration', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(values),
    });

    if (response.ok) {
      alert(
        'Vielen Dank! Die Eingabe deiner Sponsorenzusage war erfolgreich. Wir schicken dir jetzt eine Email. Bitte öffne den Link, um die Angaben zu bestätigen. (Falls du die Option "Brief" gewählt hast, ist deine Sponsorenzusage bereits hiermit abgeschlossen).',
      );
      reset();
    } else {
      alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    }
  };

  const group =
    watch('participantGroup') ||
    (!dirtyFields.participantGroup ? initialGroup : undefined);
  const groupDescriptionSource = groups.find(
    (g) => g.id === group,
  )?.registrationPageTextSource;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mt-3 space-y-2">
        <Label htmlFor="participant-group-input">
          Stamm <span className="text-red-500">*</span>
        </Label>
        <Select
          id="participant-group-input"
          {...register('participantGroup', { value: initialGroup })}
        >
          <option value="">Stamm</option>
          {groups
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
        </Select>
        <p className="text-muted-foreground text-sm">
          Bitte wähle den Stammposten aus, zu dem dein Läufer gehört
        </p>
        {errors.participantGroup && (
          <p className="text-sm text-red-500">
            {errors.participantGroup.message}
          </p>
        )}
      </div>

      {group && groupDescriptionSource && (
        <div className="mt-4">
          <MDXRemote {...groupDescriptionSource} components={mdxComponents} />
        </div>
      )}

      <h2 className="mt-8 mb-4 text-lg font-semibold">Angaben Teilnehmer</h2>
      <p className="text-muted-foreground mb-4 text-sm text-pretty">
        Trage hier Vor- und Nachnamen des Läufers ein
      </p>

      <div className="mt-3 space-y-2">
        <Label htmlFor="participant-first-name-input">
          Vorname <span className="text-red-500">*</span>
        </Label>
        <Input
          id="participant-first-name-input"
          placeholder="Vorname"
          {...register('participantFirstName')}
        />
        {errors.participantFirstName && (
          <p className="text-sm text-red-500">
            {errors.participantFirstName.message}
          </p>
        )}
      </div>

      <div className="mt-3 space-y-2">
        <Label htmlFor="participant-last-name-input">
          Nachname <span className="text-red-500">*</span>
        </Label>
        <Input
          id="participant-last-name-input"
          placeholder="Nachname"
          {...register('participantLastName')}
        />
        {errors.participantLastName && (
          <p className="text-sm text-red-500">
            {errors.participantLastName.message}
          </p>
        )}
      </div>

      <h2 className="mt-8 mb-4 text-lg font-semibold">Angaben Sponsor</h2>
      <p className="text-muted-foreground mb-4 text-sm text-pretty">
        Teile uns deinen Namen und eine Emailadresse mit, unter der wir dich
        kontaktieren dürfen. Wählst du die Kontaktmethode &quot;Brief&quot;,
        schicken wir dir das Ergebnis des Läufers per Post zu.
      </p>

      <div className="mt-3 space-y-2">
        <Label htmlFor="sponsor-first-name-input">
          Vorname <span className="text-red-500">*</span>
        </Label>
        <Input
          id="sponsor-first-name-input"
          placeholder="Vorname"
          {...register('sponsorFirstName')}
        />
        {errors.sponsorFirstName && (
          <p className="text-sm text-red-500">
            {errors.sponsorFirstName.message}
          </p>
        )}
      </div>

      <div className="mt-3 space-y-2">
        <Label htmlFor="sponsor-last-name-input">
          Nachname <span className="text-red-500">*</span>
        </Label>
        <Input
          id="sponsor-last-name-input"
          placeholder="Nachname"
          {...register('sponsorLastName')}
        />
        {errors.sponsorLastName && (
          <p className="text-sm text-red-500">
            {errors.sponsorLastName.message}
          </p>
        )}
      </div>

      <div className="mt-3 space-y-2">
        <Label htmlFor="sponsor-contact-method-input">
          Kontaktmethode <span className="text-red-500">*</span>
        </Label>
        <Select
          id="sponsor-contact-method-input"
          {...register('sponsorContactMethod', {
            value: contactMethod,
          })}
          onChange={(e) => setContactMethod(e.target.value as 'email' | 'post')}
        >
          <option value="email">Email</option>
          <option value="post">Post</option>
        </Select>
        {errors.sponsorContactMethod && (
          <p className="text-sm text-red-500">
            {errors.sponsorContactMethod.message}
          </p>
        )}
      </div>

      {contactMethod === 'email' ? (
        <div className="mt-3 space-y-2">
          <Label htmlFor="sponsor-email-input">
            Email Adresse <span className="text-red-500">*</span>
          </Label>
          <Input
            id="sponsor-email-input"
            placeholder="Email Adresse"
            {...register('sponsorEmail')}
          />
          {errors.sponsorEmail && (
            <p className="text-sm text-red-500">
              {errors.sponsorEmail.message}
            </p>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <Label htmlFor="sponsor-address-input">
            Anschrift <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="sponsor-address-input"
            placeholder="Anschrift"
            {...register('sponsorAddress')}
          />
          {errors.sponsorAddress && (
            <p className="text-sm text-red-500">
              {errors.sponsorAddress.message}
            </p>
          )}
        </div>
      )}

      <h2 className="mt-8 mb-4 text-lg font-semibold">Spendenbetrag</h2>
      <p className="text-muted-foreground mb-4 text-sm text-pretty">
        Nenne uns hier, wieviel Euro du je gelaufenen Kilometer (des Läufers) an
        den Stammposten spenden möchtest. Wenn du dich beispielsweise bereit
        erklärst, 2.5€ pro Kilometer zu spenden und der Läufer läuft insgesamt
        10km, dann bekommst du eine Spendenaufforderung für 25,00€.
      </p>

      <div className="space-y-2">
        <Label htmlFor="amount-input">
          Betrag <span className="text-red-500">*</span>
        </Label>
        <Input
          id="amount-input"
          placeholder="Betrag"
          min={0.5}
          {...register('amount')}
        />
        <p className="text-muted-foreground text-sm">
          Spendenbetrag in Euro pro gelaufenem Kilometer
        </p>
        {errors.amount && (
          <p className="text-sm text-red-500">{errors.amount.message}</p>
        )}
      </div>

      <h2 className="mt-8 mb-4 text-lg font-semibold">Sonstiges</h2>

      <div className="mt-3 space-y-2">
        <Label htmlFor="note">Kommentar</Label>
        <Textarea
          id="note"
          placeholder="Hinterlasse uns eine Notiz zu deiner Anmeldung (nicht sichtbar für Teilnehmer)"
          {...register('note')}
        />
        {errors.note && (
          <p className="text-sm text-red-500">{errors.note.message}</p>
        )}
      </div>

      <Button className="mt-8" disabled={isSubmitting} type="submit">
        {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
      </Button>
    </form>
  );
};

export default RegistrationForm;
